import React from 'react';
import type { PaginationComponentProps } from 'react-data-table-component';

export default function DataTablePagination({
  rowsPerPage,
  rowCount,
  currentPage,
  onChangePage,
  onChangeRowsPerPage,
}: PaginationComponentProps) {
  const totalPages = rowCount === 0 ? 1 : Math.ceil(rowCount / rowsPerPage);
  const firstRow = rowCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const lastRow = Math.min(currentPage * rowsPerPage, rowCount);
  const canPrevious = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-3 bg-rs-surfaceLight/50 border-t border-rs-border">
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-400">
          Filas por página:
        </span>
        <select
          value={rowsPerPage}
          onChange={e => onChangeRowsPerPage(Number(e.target.value), currentPage)}
          className="bg-rs-surfaceLight border border-rs-border rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-rs-accent/50 focus:border-rs-accent"
        >
          {[5, 10, 15, 20, 25, 50].map(n => (
            <option key={n} value={n} className="bg-rs-surface text-gray-200">
              {n}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-500 hidden sm:inline">
          {firstRow}-{lastRow} de {rowCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChangePage(1, rowCount)}
          disabled={!canPrevious}
          className="p-2 rounded-lg text-gray-400 hover:bg-rs-surfaceLight hover:text-rs-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
          title="Primera página"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onChangePage(currentPage - 1, rowCount)}
          disabled={!canPrevious}
          className="p-2 rounded-lg text-gray-400 hover:bg-rs-surfaceLight hover:text-rs-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
          title="Anterior"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <span className="min-w-[80px] text-center text-sm text-gray-300 font-medium">
          Pág. {currentPage} / {totalPages || 1}
        </span>

        <button
          type="button"
          onClick={() => onChangePage(currentPage + 1, rowCount)}
          disabled={!canNext}
          className="p-2 rounded-lg text-gray-400 hover:bg-rs-surfaceLight hover:text-rs-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
          title="Siguiente"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onChangePage(totalPages, rowCount)}
          disabled={!canNext}
          className="p-2 rounded-lg text-gray-400 hover:bg-rs-surfaceLight hover:text-rs-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
          title="Última página"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}
